import GVCompletions from "./GVCompletions";
import { useState, useContext } from "react";
import GVMapView from "./GVMapView";
import GVHybridView from "./GVHybridView";
import GraphicalViewerMapProvider from "../context/GraphicalViewerMapContext";
import Icon from "../../components/Icon";
import GVSettingsModal from "../components/GVSettingsModal";
import { PauseIcon, PlayIcon } from "../components/PlayPauseIcons";
import GraphicalViewerProvider, {
    GraphicalViewerContext,
} from "../../contexts/GraphicalViewerContext";
import { PageHeader } from "../components/GraphicalViewer/PageHeader";
import GVKeyboardModal from "../components/GraphicalViewer/GVKeyboardModal";
import { AdminAuctionContext } from "../context/AdminAuctionContext";
import { RefreshIcon } from "@heroicons/react/solid";
import classNames from "../../utilities/classNames";
import { formatRelative } from "date-fns";
import { usePusher } from "@harelpls/use-pusher";
import { useEffect } from "react";

// This page will have three different views to see current Auction status:
//      1. Table View (Completions and Bids)
//      2. Map View (Tracts and Bidders)
//      3. Hybrid View (Both)
//
// This page will also utilize shortcuts to quickly navigate to different views of the page.
export default function GraphicalViewerBase() {
    return (
        <GraphicalViewerProvider>
            <GraphicalViewer />
        </GraphicalViewerProvider>
    );
}

function GraphicalViewer() {
    const {
        bidderTractInfo,
        setSettings,
        getSavedSettings,
        showTableView,
        showMapView,
        showHybridView,
        completionIndex,
        completionColors,
        setIsPlaying,
        isPlaying,
        showIsPlaying,
        countdown,
        isCountingDown,
        progress,
        completionsQuery,
    } = useContext(GraphicalViewerContext);

    const [showKeyboardModal, setKeyboardModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const { client } = usePusher();
    const { currentAuctionIsLive } = useContext(AdminAuctionContext);
    const [connectionStatus, setConnectionStatus] = useState(client.connection.state);

    const requestFullscreen = async () => {
        try {
            if (document.documentElement.requestFullscreen) {
                await document.documentElement.requestFullscreen();
            } else if (document.documentElement.webkitRequestFullscreen) {
                document.documentElement.webkitRequestFullscreen();
            } else if (document.documentElement.msRequestFullscreen) {
                document.documentElement.msRequestFullscreen();
            }
        } catch (err) {
            console.error('Fullscreen error:', err);
        }
    };

    const exitFullscreen = () => {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            const isNowFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
            setIsFullscreen(isNowFullscreen);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        };
    }, []);

    const handleSettingsModalConfirm = () => {
        setSettings(getSavedSettings());
        setShowSettingsModal(false);
    };

    const forceReconnect = () => {
        console.warn("Forcing Pusher to reconnect...");
        client.disconnect();
        setTimeout(() => {
            client.connect();
        }, 1000);
    }

    // More robust connection state handling
    useEffect(()=>{
        const stateChange = ({ previous, current }) => {
            setConnectionStatus(current);
            if (current === 'failed' || current === 'unavailable') {
                console.warn('Pusher failed to reconnect. Trying manually...');
                client.disconnect();
                setTimeout(() => {
                    client.connect();
                }, 1000);
            }

            if (current === "connected") {
                completionsQuery.refetch();
            }
        };

        client.connection.bind('state_change', stateChange);

        return () => {
            client.connection.unbind('state_change', stateChange);
        }
    }, [client, completionsQuery])

    return (
        <GraphicalViewerMapProvider>
            <div className="flex flex-col justify-between h-screen">
                <div>
                    {!showHybridView && <PageHeader />}
                    {showMapView && (
                        <div>
                            <GVMapView bidderTractInfo={bidderTractInfo} />
                        </div>
                    )}
                    {showTableView && <GVCompletions />}
                    {showHybridView && <GVHybridView />}
                </div>
                <div id="gv-footer">
                    {isCountingDown && (
                        <div className="text-right h-4">
                            <p>Next page in: {countdown}s</p>
                        </div>
                    )}
                    <div className="w-full h-2 bg-gray-200">
                        <div
                            className="h-full transition-all"
                            style={{
                                width: `${100 - progress}%`,
                                backgroundColor:
                                    completionColors[completionIndex] ||
                                    "#6b7280",
                            }}
                        />
                    </div>
                    <div className="flex items-center justify-between w-full h-12 text-xl font-bold text-white bg-gray-700">
                        <div className="flex items-center h-full px-4 border-r border-white">
                            <div
                                className={`flex justify-center cursor-pointer min-w-44 ${
                                    showIsPlaying
                                        ? ""
                                        : "pointer-events-none opacity-50"
                                }`}
                                onClick={() => setIsPlaying(!isPlaying)}
                            >
                                {!isPlaying ? <PlayIcon /> : <PauseIcon />}
                            </div>
                        </div>
                        <div className="flex items-center h-full">
                            {/* Status icon for websocket connection */}
                            <div className="flex items-center h-full px-2 mr-2">
                                <div
                                    onClick={forceReconnect}
                                    className={classNames(
                                        "w-4 h-4 rounded-full cursor-pointer",
                                        connectionStatus ===
                                            "connected" && "bg-green-500 opacity-0",
                                        connectionStatus === "connecting" &&
                                            "bg-yellow-500 animate-pulse",
                                        connectionStatus !== "connected" && connectionStatus !== "connecting" &&
                                            "bg-red-500 animate-pulse"
                                    )}
                                ></div>
                            </div>
                            <div
                                className={`px-3 bg-gray-700 font-bold text-base cursor-pointer flex items-center justify-center ${
                                    !currentAuctionIsLive
                                        ? "pointer-events-none opacity-50 disabled"
                                        : ""
                                }`}
                                onClick={() => {
                                    completionsQuery.refetch();
                                }}
                            >
                                <span className=" hidden mr-2 text-gray-500 text-xs">
                                    {completionsQuery.data?.length > 0
                                        ? "Last refreshed: " +
                                          // Show relative time
                                          formatRelative(
                                              new Date(
                                                  completionsQuery.dataUpdatedAt
                                              ),
                                              new Date()
                                          )
                                        : ""}
                                </span>
                                <RefreshIcon
                                    className={classNames(
                                        "w-6 h-6 ",
                                        completionsQuery.isFetching ||
                                            completionsQuery.isRefetching
                                            ? "animate-spin"
                                            : ""
                                    )}
                                />
                            </div>
                            <div className="flex items-center h-full pl-4 pr-4 border-l border-r border-white">
                                <div
                                    className="cursor-pointer"
                                    onClick={() => setKeyboardModal(true)}
                                >
                                    <Icon type="info" className="block" />
                                </div>
                            </div>
                            <div className="flex items-center h-full pl-4 pr-4 border-l border-white">
                                <div
                                    className="cursor-pointer"
                                    onClick={() => setShowSettingsModal(true)}
                                >
                                    <Icon type="cog" className="block" />
                                </div>
                            </div>
                            <div className="flex items-center h-full pl-4 pr-4 border-l border-white">
                                <div
                                    className="cursor-pointer"
                                    onClick={() => isFullscreen ? exitFullscreen() : requestFullscreen()}
                                    title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                                >
                                    <Icon type="arrows-expand" className="block" />
                                </div>
                            </div>
                            <div
                                className="px-4 cursor-pointer border-l border-white h-full flex items-center justify-center"
                                onClick={() => window.close()}
                            >
                                EXIT
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <GVSettingsModal
                open={showSettingsModal}
                onCancel={() => setShowSettingsModal(false)}
                onConfirm={() => handleSettingsModalConfirm()}
            />
            <GVKeyboardModal
                open={showKeyboardModal}
                onCancel={() => setKeyboardModal(false)}
            />
        </GraphicalViewerMapProvider>
    );
}
